import { Soundfont, useAudioEngine, useInstrument } from '@tunasong/audio-ui'
import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { useEffect, useMemo, useRef, useState } from 'react'
import { melodyInstrument } from './instrument-hint.js'

/**
 * Play a melody with its feel, and run a play head across the roll.
 *
 * The timing comes from `MelodyLib.scheduleMelody` - pure and tested to the millisecond without
 * audio - and goes straight to `engine.playScheduled`, which puts every note on the Tone transport
 * at its own absolute time. Nothing on this path rounds a millisecond. `playSequence` could not be
 * used: it walks note-to-note by duration and has no absolute time, so a 12 ms offset would be
 * silently discarded at the one place the offset means anything.
 *
 * The play head is a separate concern from the audio: a `requestAnimationFrame` loop over
 * wall-clock time, deliberately not read back from the audio clock. It is a cursor, not a meter.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.2
 */

export interface UseMelodyRollPlaybackProps {
  melody: Melody
  /** Play with `offsetMs` treated as zero, so the feel can be A/B'd. */
  quantized?: boolean
}

export interface MelodyRollPlayback {
  play: () => void
  stop: () => void
  isPlaying: boolean
  /** ms from the start of the melody, or null when stopped. */
  playheadMs: number | null
}

export const useMelodyRollPlayback = (props: UseMelodyRollPlaybackProps): MelodyRollPlayback => {
  const { melody, quantized = false } = props
  const engine = useAudioEngine()
  const instrumentName = melodyInstrument(melody.instrumentHint)

  /**
   * One Soundfont per instrument name, not per render: `useInstrument` registers what it is given,
   * and a fresh object each render would churn the engine's instrument table. Same shape as
   * `usePlayNotes`.
   */
  const instrument = useMemo(
    () => new Soundfont(engine.mixer.context, instrumentName),
    [engine.mixer.context, instrumentName]
  )

  /**
   * Route to THIS hook's instrument. `engine.playScheduled(events)` with no id resolves to the
   * first instrument registered on the page, so a melody on a page that also has a piano keyboard
   * would sound on whichever mounted first - and the failure is silent. @see useInstrument
   */
  const instrumentId = useInstrument('melody', instrument) ?? undefined

  const [playheadMs, setPlayheadMs] = useState<number | null>(null)
  const frame = useRef<number | null>(null)

  const stop = () => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current)
      frame.current = null
    }
    setPlayheadMs(null)
  }

  useEffect(() => stop, [])

  const play = () => {
    stop()
    const events = MelodyLib.scheduleMelody(melody, { quantized })
    if (events.length === 0) {
      return
    }
    /*
     * `ScheduledMelodyEvent.time` is ms from the start of the melody; `ScheduledNote.timeMs` is ms
     * from the schedule call. They are the same number here - playback starts at the melody's
     * start - but the two names describe different origins, so the mapping is explicit.
     */
    engine.playScheduled(
      events.map(event => ({
        midi: event.midi,
        velocity: event.velocity,
        timeMs: event.time,
        durationMs: event.durationMs,
      })),
      instrumentId
    )

    const totalMs = MelodyLib.melodyDurationMs(melody, { quantized })
    const startedAt = performance.now()
    const tick = () => {
      const elapsed = performance.now() - startedAt
      if (elapsed >= totalMs) {
        stop()
        return
      }
      setPlayheadMs(elapsed)
      frame.current = requestAnimationFrame(tick)
    }
    setPlayheadMs(0)
    frame.current = requestAnimationFrame(tick)
  }

  return { play, stop, isPlaying: playheadMs !== null, playheadMs }
}
